import { Box, IconButton, TextField, Typography } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { FC } from 'react';

import { isNotEmptyString, isNullOrUndefined } from '@vy/utils';

import { useClickToEditCustomFieldValue } from './hooks/use-click-to-edit-custom-field-value';

const EMPTY_PLACEHOLDER = '--';

const formatDisplayValue = (value: unknown): string => {
  if (isNullOrUndefined(value)) {
    return EMPTY_PLACEHOLDER;
  }
  const stringValue = `${value}`;
  return isNotEmptyString(stringValue) ? stringValue : EMPTY_PLACEHOLDER;
};

type ClickToEditTextCellProps = {
  valueType: 'String' | 'Number';
  value: unknown;
  onCommit: (next: string | number | null) => void;
  onBlur: () => void;
  dataVyPrefix: string;
  showConfirmButtons: boolean;
};

export const ClickToEditTextCell: FC<ClickToEditTextCellProps> = ({
  valueType,
  value,
  onCommit,
  onBlur,
  dataVyPrefix,
  showConfirmButtons,
}) => {
  const {
    isEditing,
    draft,
    setDraft,
    inputRef,
    enterEditMode,
    commitDraft,
    cancelEdit,
    handleKeyDown,
  } = useClickToEditCustomFieldValue({ value, valueType, onCommit, onBlur });

  if (!isEditing) {
    return (
      <Box
        data-vy={`${dataVyPrefix}-display`}
        onClick={enterEditMode}
        sx={{
          cursor: 'pointer',
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <Typography variant="body2">{formatDisplayValue(value)}</Typography>
      </Box>
    );
  }

  if (showConfirmButtons) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <TextField
          data-vy={`${dataVyPrefix}-input`}
          inputRef={inputRef}
          value={draft}
          type={valueType === 'Number' ? 'number' : 'text'}
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={handleKeyDown}
          size="small"
          variant="standard"
          sx={{ width: 200 }}
        />
        <IconButton onClick={commitDraft} size="small">
          <CheckIcon color="success" sx={{ fontSize: '20px' }} />
        </IconButton>
        <IconButton onClick={cancelEdit} size="small">
          <CloseIcon color="inherit" sx={{ fontSize: '20px' }} />
        </IconButton>
      </Box>
    );
  }

  return (
    <TextField
      data-vy={`${dataVyPrefix}-input`}
      inputRef={inputRef}
      value={draft}
      type={valueType === 'Number' ? 'number' : 'text'}
      onChange={(event) => setDraft(event.target.value)}
      onBlur={commitDraft}
      onKeyDown={handleKeyDown}
      size="small"
      variant="standard"
      sx={{ width: 200 }}
    />
  );
};
